import styled from "@emotion/styled";
import type { ReactNode } from "react";
import { useCallback, useEffect, useEffectEvent, useState, type MouseEvent } from "react";

import {
  DEFAULT_INPUT_HEIGHT,
  FONT_WEIGHT_SEMI_BOLD,
  FormField,
  Icon,
  IconButton,
  Popover,
  PropertiesPanelPopoverContent,
  SkiliticsIcon,
  Tooltip,
  transientOptions,
  useCommitChange,
  useField,
  useFieldContext,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import type { PrimitiveValue, PrimitiveValueName, ValueNameToType } from "@skilitics/nautilus-core";
import { isReferencedValue, type ValueConfigFor } from "@skilitics/nautilus-core-designer-data";

import { defaultValueForConfig } from "../defaultValueForConfig.ts";
import type { ValueSubtypeRequirement } from "../valueSubtypes.ts";
import { FieldBindingPicker } from "./BindingPicker.tsx";
import { useValueBindChoices } from "./useValueBindChoices.ts";
import { useValueConfigSummary } from "./useValueConfigSummary.ts";
import { useValueSubtypeMismatch } from "./useValueSubtypeMismatch.ts";

const BindButton = styled(IconButton, transientOptions)<{ $bound: boolean }>`
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  transition: opacity 0.1s ease-in-out;
  height: 22px;
  width: 22px;
  font-size: 12px;

  ${({ $bound, theme }) =>
    $bound
      ? `
        opacity: 1;
        color: ${theme.primaryStrong};
      `
      : `
        opacity: 0;
        background-color: ${theme.surface};

        &:hover {
          background-color: ${theme.surfaceVariant};
        }
      `}

  &:focus-visible {
    opacity: 1;
  }
`;

const ControlContainer = styled.div`
  flex: 1;
  min-width: 0;

  > div,
  .MuiTextField-root {
    width: 100%;
  }
`;

const BoundValue = styled("div", transientOptions)<{ $mismatched: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  height: ${DEFAULT_INPUT_HEIGHT}px;
  padding: 0 20px 0 4px;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  border: 1px solid ${({ $mismatched, theme }) => ($mismatched ? theme.warning : theme.primaryStrong)};
  border-radius: 4px;
  color: ${({ $mismatched, theme }) => ($mismatched ? theme.onWarningContainer : theme.primaryStrong)};
  background: ${({ $mismatched, theme }) => ($mismatched ? theme.warningContainer : theme.primaryContainer)};
  cursor: pointer;
  overflow: hidden;
`;

const BoundValueLabel = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
`;

/**
 * `$compact` is for a control that sizes to its content (a boolean's switch): the
 * row stops stretching so the bind button sits beside the control rather than
 * stranded at the far right, and with nothing beneath it there is nothing to
 * reveal on hover.
 */
const Row = styled("div", transientOptions)<{ $compact: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;

  &:hover ${BindButton} {
    opacity: 1;
  }

  ${({ $compact }) =>
    $compact &&
    `
      gap: 6px;

      ${ControlContainer} {
        flex: 0 1 auto;

        > div {
          width: auto;
        }
      }

      ${BindButton} {
        position: static;
        transform: none;
        opacity: 1;
        background-color: transparent;
      }

      ${BoundValue} {
        padding-right: 4px;
      }
    `}
`;

const StyledPopover = styled(Popover)`
  .MuiPaper-root {
    width: 280px;
    overflow: visible;
    border-radius: 8px;
    margin-top: 4px;
  }
`;

const PopoverContent = styled(PropertiesPanelPopoverContent)`
  &:after {
    display: none;
  }
`;

const BindBody = styled.div`
  padding: 4px;
`;

export type ValueConfigEditorProps<TName extends PrimitiveValueName> = {
  type: TName;
  /**
   * narrows the offer within the base type — only values declaring a satisfying
   * subtype are offered, and a stored binding that fails it is flagged. Build it
   * here beside `type`, from this site's own config, and memoise it.
   */
  subtype?: ValueSubtypeRequirement;
  /** which node's scope determines the values on offer (e.g. the trigger's node) */
  scopeNodeId?: number;
  /** a node whose own values to leave out of the offer (usually the node holding this value) */
  excludeNodeId?: number;
  /**
   * The node whose config holds this value, where it isn't `excludeNodeId`. Only used to refuse a
   * pick that loops back to it; without either, the check is skipped rather than guessed at.
   */
  forNodeId?: number;
  /** false where binding this value would have no effect, leaving just the static control */
  bindable?: boolean;
  /**
   * The literal to seed where a binding goes away — unbound by the author, or
   * cleared as stranded. Defaults to the type's own. Passed in rather than looked
   * up, so this stays usable from a form that has no signature to look one up from.
   */
  defaultLiteral?: PrimitiveValue;
  disabled?: boolean;
  /** the static-literal control (a standard Field*), bound to the same field */
  children: ReactNode;
};

/**
 * Edits one value config (use inside a FormRowField), with a Field per branch:
 * `children` — the caller's own — edits a literal right here, and a bound-value
 * chip with a picker behind it edits a ReferencedValue pointing over there. Which
 * branch is mounted comes from the stored value itself, so the input on screen is
 * always the one for the value it is handed.
 *
 * Where `bindable`, a toggle beside the input switches branch: binding stores a
 * ReferencedValue, unbinding resets to a type-appropriate literal. Where it is
 * not, only the literal branch renders, with no bind chrome — and a binding
 * already stored is cleared, since `children` has nothing to show for a reference.
 *
 * `type` and `subtype` state what this field accepts: they filter the offer, and a
 * stored binding that no longer satisfies them is flagged in place rather than
 * cleared, since it is still renderable and only the author knows what they meant.
 */
export const ValueConfigEditor = <TName extends PrimitiveValueName>({
  type,
  subtype,
  scopeNodeId,
  excludeNodeId,
  forNodeId = excludeNodeId,
  bindable = true,
  defaultLiteral,
  disabled = false,
  children,
}: ValueConfigEditorProps<TName>) => {
  const formatMessage = useFormatMessage(formatStrings);
  const choices = useValueBindChoices({ type, subtype, scopeNodeId, excludeNodeId });
  const summariseValueConfig = useValueConfigSummary();
  const valueSubtypeMismatch = useValueSubtypeMismatch();
  const commitChange = useCommitChange();

  const { dataTest, name } = useFieldContext();
  const [{ value }, , { setValue }] = useField<ValueConfigFor<TName> | undefined>(name);

  const storedConfig = isReferencedValue(value) ? value : undefined;
  const bound = storedConfig !== undefined;
  const mismatched = valueSubtypeMismatch(value, subtype);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const bindDataTest = dataTest ?? "value-bind";

  const seedLiteral = defaultLiteral ?? defaultValueForConfig(type);

  const handleUnbind = useCallback(() => {
    setValue(seedLiteral as ValueNameToType<TName>)
      .then(() => commitChange())
      .catch(console.warn);
    setAnchorEl(null);
  }, [commitChange, seedLiteral, setValue]);

  const handleClosePopover = useCallback(() => setAnchorEl(null), []);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  // A value that can no longer be bound must not stay bound: the static control is
  // handed the raw field value and has nothing to show for a reference, and a
  // reference left stored still feeds design-time resolution and cycle detection.
  const clearStrandedBinding = useEffectEvent(() => {
    setValue(seedLiteral as ValueNameToType<TName>)
      .then(() => commitChange())
      .catch(console.warn);
  });

  const stranded = !bindable && bound;
  useEffect(() => {
    if (stranded) clearStrandedBinding();
  }, [stranded]);

  // nothing to bind to, or binding wouldn't take effect: just the static control,
  // withheld for the frame it would still be showing a reference. A binding
  // already stored keeps its chrome even with nothing on offer — otherwise
  // `children` would be handed the reference, and there would be no way to unbind.
  if (!bindable || (!choices.options.length && !bound)) return stranded ? null : <>{children}</>;

  return (
    <>
      <Row $compact={type === "boolean"}>
        <ControlContainer>
          {bound ? (
            <Tooltip
              title={mismatched ? formatMessage("incompatibleTooltip") : summariseValueConfig(value)}
              placement="top"
            >
              <BoundValue
                $mismatched={mismatched}
                onClick={handleOpen}
                data-test={dataTest && `${dataTest}-bound`}
                data-test-incompatible={mismatched || undefined}
              >
                {mismatched ? <Icon icon="exclamation-circle" size={16} /> : <SkiliticsIcon icon="link" size={16} />}
                <BoundValueLabel>{summariseValueConfig(value)}</BoundValueLabel>
              </BoundValue>
            </Tooltip>
          ) : (
            children
          )}
        </ControlContainer>
        <BindButton
          $bound={bound}
          title={formatMessage(bound ? "unbindTooltip" : "bindTooltip")}
          data-test={dataTest ? `${dataTest}-bind` : "value-bind"}
          onClick={bound ? handleUnbind : handleOpen}
          disabled={disabled}
        >
          <SkiliticsIcon icon={bound ? "unlink" : "link"} size={14} />
        </BindButton>
      </Row>

      <StyledPopover
        id={`popover-${name}-value-bind`}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <PopoverContent onClose={handleClosePopover} header={formatMessage("bindHeader")}>
          <BindBody>
            {/* on the caller's own field; `explicitLabel` suppresses the caller's label, the header names it */}
            <FormField name={name} dataTest={bindDataTest} explicitLabel>
              <FieldBindingPicker
                choices={choices}
                forNodeId={forNodeId}
                disabled={disabled}
                label=""
                placeholder={formatMessage("valuePlaceholder")}
              />
            </FormField>
          </BindBody>
        </PopoverContent>
      </StyledPopover>
    </>
  );
};

const formatStrings = {
  bindHeader: {
    en: "Bind Value",
  },
  valuePlaceholder: {
    en: "Select Value",
  },
  bindTooltip: {
    en: "Bind to a value",
  },
  unbindTooltip: {
    en: "Revert to a static value",
  },
  incompatibleTooltip: {
    en: "This bound value is no longer compatible with this field. Pick another, or revert to a static value.",
  },
} satisfies FormatMessages;
